import { MIXTURE, SCENARIO_EP, SUBSTANCE } from 'utils/constants/coreConstants';
import { PC } from 'utils/constants/descriptorTypes';

/**
 * Three of the arguments below are indexes that stand in for fields the payloads
 * used to carry inside their parent:
 *
 *   compositionByProduct  <- product.product_raw_materials
 *   substancesByMixture   <- mixture.substances      (mixture_substance by mixture_id)
 *   mixturesBySubstance   <- substance.mixtures      (mixture_substance by substance_id)
 *
 * The last two read backwards, and swapping them still produces a full table of
 * plausible rows, so they are named after the field they replace. Every read of
 * them goes through `|| []`: an index has no key for a parent with no rows, and
 * the fields they replace were always arrays.
 *
 * `compositionByProduct` took the place of `productsById` rather than being added
 * next to it, because the product itself was only ever read for that one field.
 */
export default (
    conditionsById, descriptorsById, descriptorFilter, scenario, lcs, productId, compositionByProduct,
    rawMaterialsById, scenarioDescriptors, scenarioDescriptorConditions, scenarios, shipmentsById, unitsById,
    substancesByMixture, mixturesBySubstance
) => {

    const productComposition = compositionByProduct[productId] || [];

    /**
     * Start By filtering the PC scenarios descriptors for PPRODUCT SCENARIOS.
     * This will later be used to filter out the CE DE MP - SCENARIOS which substances
     * enter in the product but don't share the intital PC, and because of that
     * should be filtered out
     */
    const filteredScenarioDescriptors = scenarioDescriptors.filter(sd =>
        sd.scenario_id === scenario.id &&
        descriptorsById[sd.descriptor_id].descriptor_type_id === PC
    );
    const descriptorsPCidsToFilterCEDEMP = filteredScenarioDescriptors.map(fsd => fsd.descriptor_id);

    /**
     * Algorithm to filter:
     *  - Subtances from mixtures in CARGA
     *  - Substances in CARGA
     *  - Substances in MP FINAL
     *  ----------------------------------------------
     */

    const summaryRawMaterials = [];
    /**
     * First adding all substances related with mixtures, assuming that the substance
     * has not been modified in exclusions AND all substances that have code (CARGA)
     **/
    productComposition.forEach(prm => {

        if (prm.added_to_charge) {

            if (rawMaterialsById[prm.raw_material_id].type === MIXTURE) {
                (substancesByMixture[prm.raw_material_id] || []).forEach(interSubstance => {
                    const substanceId = interSubstance.substance_id;
                    summaryRawMaterials.push({
                        ...rawMaterialsById[substanceId],
                        raw_material_final: false,
                        added_to_charge: true,
                        excluded_from_final_product: false,
                        product_raw_material_id: null, // No id yet: whether this link exists is not known here
                        contributes_to_hazard: false,
                        code: rawMaterialsById[prm.raw_material_id].code,
                        mixture_name: rawMaterialsById[prm.raw_material_id].name,
                        mixture_id: prm.raw_material_id,
                        mixture_substance_id: interSubstance.id
                        // mixture_substance_id: prm.raw_material_id,
                        // id_produto_mp_mistura_caminho: prm.id
                    });
                });
            } else if (!prm.mixture_id) {
                summaryRawMaterials.push({
                    ...rawMaterialsById[prm.raw_material_id],
                    raw_material_final: prm.raw_material_final,
                    added_to_charge: prm.added_to_charge,
                    excluded_from_final_product: prm.excluded_from_final_product,
                    product_raw_material_id: prm.id,
                    contributes_to_hazard: prm.contributes_to_hazard,
                    mixture_name: '',
                    mixture_id: null,
                    mixture_substance_id: null
                });
            }
        }
    });


    /**
     * Finding Substances that overlap with the default/lazy ones created above
     * and putting these ones on top
     **/
    productComposition.forEach(prm => {
        // TODO removed prm.added_to_charge from IF because is unclear how values come tagged from the API
        //if (prm.mixture_id && prm.added_to_charge) {
        if (prm.mixture_id) {

            const index = summaryRawMaterials.findIndex(s =>
                s.id === prm.raw_material_id &&
                s.mixture_id === prm.mixture_id
            );
            if (index >= 0) {
                summaryRawMaterials[index] =
                    {
                        ...summaryRawMaterials[index],
                        product_raw_material_id: prm.id,
                        excluded_from_final_product: prm.excluded_from_final_product,
                        contributes_to_hazard: prm.contributes_to_hazard
                    };
            }
        }
    });
    /**
     * Finding Substances with raw_material_final True (FINAL)
     * The above is incorrect, raw_material_final doesn't seem to make sense
     */
    productComposition.forEach(prm => {
        if (!prm.added_to_charge && !prm.mixture_id && rawMaterialsById[prm.raw_material_id].type === SUBSTANCE) {
            summaryRawMaterials.push({
                ...rawMaterialsById[prm.raw_material_id],
                raw_material_final: prm.raw_material_final,
                added_to_charge: prm.added_to_charge,
                excluded_from_final_product: prm.excluded_from_final_product,
                product_raw_material_id: prm.id,
                contributes_to_hazard: prm.contributes_to_hazard,
                code: null,
                mixture_name: null
            });
        }
    });
    /**
     * END - * Algorithm to filter
     *  ----------------------------------------------
     */

    /**
     * Filter summaryRawMaterials to get summarySubstances unrepeated
     */
    const summarySubstances = [];
    summaryRawMaterials
        .filter(rm =>
            rm.contributes_to_hazard || rm.raw_material_final
        )
        .forEach(rm => {
            if (summarySubstances.findIndex(substance => substance.id === rm.id) === -1) {
                summarySubstances.push(rm);
            }
        });


    /**
     * -----------------------------------------------------
     * Finding the EP scenarios for the substances above
     */
    let summarySubstanceScenarios = [];

    summarySubstanceScenarios = summarySubstances.map(substance => {
        return scenarios.filter(s =>
            s.type === SCENARIO_EP &&
            s.substance_id === substance.id &&
            s.raw_material_supplier_id &&
            parseInt(s.lcs_id) === lcs.id &&
            (
                substance.id === shipmentsById[s.raw_material_supplier_id].raw_material_id ||
                (mixturesBySubstance[substance.id] || [])
                    .map(mix => mix.mixture_id)
                    .includes(shipmentsById[s.raw_material_supplier_id].raw_material_id)
            )
        );
    });

    /**
     * Finding the descriptors PC (To Compare Later and filter CEDEMP) that match
     * this scenarios and adding to summarySubstanceScenarios -> descriptorsPCfilterCEDEMP
     */
    summarySubstances.forEach((substance, index) => {
        summarySubstanceScenarios[index].forEach(substanceScenario => {
            substanceScenario.descriptorsPCfilterCEDEMP = scenarioDescriptors
                .filter(scenarioDescriptor =>
                    scenarioDescriptor.scenario_id === substanceScenario.id &&
                    descriptorsById[scenarioDescriptor.descriptor_id].descriptor_type_id === PC
                )
                .map(scenarioDescriptor => scenarioDescriptor.descriptor_id);
        });
    });

    /**
     * Finding the descriptors that match this scenarios and adding to
     * summarySubstanceScenarios
     */
    summarySubstances.forEach((substance, index) => {
        summarySubstanceScenarios[index].forEach(substanceScenario => {
            substanceScenario.descriptors = scenarioDescriptors
                .filter(scenarioDescriptor =>
                    scenarioDescriptor.scenario_id === substanceScenario.id &&
                    descriptorsById[scenarioDescriptor.descriptor_id].descriptor_type_id === descriptorFilter
                )
                .map(scenarioDescriptor => scenarioDescriptor.descriptor_id);
        });
    });

    /**
     * Finding the conditions that match this scenarios descriptors and adding to
     * summarySubstanceScenarios
     */
    summarySubstances.forEach((substance, index) => {
        summarySubstanceScenarios[index].forEach(substanceScenario => {
            substanceScenario.scenarioConditions = {};
            scenarioDescriptors
                .filter(scenarioDescriptor =>
                    scenarioDescriptor.scenario_id === substanceScenario.id &&
                    descriptorsById[scenarioDescriptor.descriptor_id].descriptor_type_id === descriptorFilter
                )
                .forEach(scenarioDescriptor => {
                    substanceScenario.scenarioConditions[scenarioDescriptor.descriptor_id] =
                        scenarioDescriptorConditions
                            .filter(sdc => scenarioDescriptor.id === sdc.scenario_descriptor_id);
                });
        });
    });

    /**
     * Now should add another layer to confront if CEDEMP Scenario contains the same
     * PC descriptors present in the PRODUCT Scenario
     */

    const summarySubstanceScenariosFinal = [];
    summarySubstanceScenarios.forEach((substanceScenarios, index) => {
        summarySubstanceScenariosFinal.push([]);
        substanceScenarios.forEach(scenario => {
            if (
                descriptorsPCidsToFilterCEDEMP.some(dId => scenario.descriptorsPCfilterCEDEMP.includes(dId))
            ) {
                summarySubstanceScenariosFinal[index].push(scenario);
            }
        });
    });

    summarySubstanceScenarios = summarySubstanceScenariosFinal;

    /**
     * -----------------------------------------------------
     */


    /**
     * Using summarySubstances and summarySubstanceScenarios (with the descriptor ids inside)
     * And
     * inverting it into an object index by descriptorIds with the paired information
     * of substances and scenarios
     */
    const productDescriptors = {};
    summarySubstanceScenarios.forEach((scenarios, substanceIndex) =>
        scenarios.forEach(scenario =>
            scenario.descriptors.forEach(descriptor => {
                const descriptorInfo = {
                    substance: summarySubstances[substanceIndex],
                    scenario: scenario
                };

                if (productDescriptors[descriptor]) {
                    productDescriptors[descriptor].push(descriptorInfo);
                } else {
                    productDescriptors[descriptor] = [descriptorInfo];
                }
            })
        )
    );

    /**
     * Using summarySubstances and summarySubstanceScenarios
     * (with the descriptor and condition ids inside)
     * And
     * inverting it into an object index by descriptorIds and by nested
     * conditioIds with the paired information of substances and scenarios
     */
    const productConditionsByDescriptorId = {};
    Object.keys(productDescriptors).forEach(descriptorId => {

        summarySubstanceScenarios.forEach((scenarios, substanceIndex) => {
            scenarios.forEach(scenario => {
                if (scenario.descriptors.includes(parseInt(descriptorId))) {
                    scenario.scenarioConditions[descriptorId].forEach(scenarioCondition => {
                        const conditionInfo = {
                            substance: summarySubstances[substanceIndex],
                            scenario: scenario
                        };

                        if (!productConditionsByDescriptorId[descriptorId]) {
                            productConditionsByDescriptorId[descriptorId] = {};
                        }

                        if (productConditionsByDescriptorId[descriptorId][scenarioCondition.condition_id]) {
                            productConditionsByDescriptorId[descriptorId][scenarioCondition.condition_id]
                                .push(conditionInfo);
                        } else {
                            productConditionsByDescriptorId[descriptorId][scenarioCondition.condition_id] =
                                [conditionInfo];
                        }
                    });
                }
            });
        });
    });

    /**
     * DESCRIPTORS
     * Merging descriptors with substance and shipment information, important! not
     * scenario information SHIPMENT
     *
     */
    const augmentedDescriptors = [];
    Object.keys(productDescriptors).forEach(descriptorId => {

        const augmentedDescriptor = {
            substanceIds: [],
            substances: [],
            shipmentIds: [],
            shipmentsBySubstanceId: {}
        };
        /**
         * Merge scenarios from the same substance, important different scenarios of the same substance might have the
         * same shipment ID, That is what is relevant here, the SUBSTANCE -- SHIPMENT so multiple scenarios with the
         * same shipment are irrelevant
         */
        productDescriptors[descriptorId].forEach(descriptorInfo => {
            if (augmentedDescriptor.substanceIds.includes(descriptorInfo.substance.id)) {
                if (!augmentedDescriptor.shipmentIds.includes(descriptorInfo.scenario.raw_material_supplier_id)) {
                    augmentedDescriptor.shipmentsBySubstanceId[descriptorInfo.substance.id].push(
                        shipmentsById[descriptorInfo.scenario.raw_material_supplier_id]
                    );
                }
            } else {
                augmentedDescriptor.substanceIds.push(descriptorInfo.substance.id);
                augmentedDescriptor.substances.push(descriptorInfo.substance);
                augmentedDescriptor.shipmentIds.push(descriptorInfo.scenario.raw_material_supplier_id);
                augmentedDescriptor.shipmentsBySubstanceId[descriptorInfo.substance.id] = [
                    shipmentsById[descriptorInfo.scenario.raw_material_supplier_id]
                ];
            }
        });

        /**
         * linking descriptors with respective SUBSTANCE -- SHIPMENT information
         */
        augmentedDescriptors.push({
            ...descriptorsById[descriptorId],
            ...augmentedDescriptor
        });
    });


    /**
     * CONDITIONS
     * Merging conditions with substance and shipment information, important! not
     * scenario information SHIPMENT
     *
     */

    const augmentedConditionsByDescriptorId = {};
    Object.keys(productConditionsByDescriptorId).forEach(descriptorId => {
        augmentedConditionsByDescriptorId[descriptorId] = {};

        Object.keys(productConditionsByDescriptorId[descriptorId]).forEach(conditionIdStr => {

            const conditionId = parseInt(conditionIdStr);
            const augmentedCondition = {
                substanceIds: [],
                substances: [],
                shipmentIds: [],
                shipmentsBySubstanceId: {}
            };
            /**
             * Merge scenarios from the same substance, important different scenarios of the same substance might have
             * the same shipment ID, That is what is relevant here, the SUBSTANCE -- SHIPMENT so multiple scenarios
             * with the same shipment are irrelevant
             */
            productConditionsByDescriptorId[descriptorId][conditionId].forEach(conditionInfo => {
                if (augmentedCondition.substanceIds.includes(conditionInfo.substance.id)) {
                    if (!augmentedCondition.shipmentIds.includes(conditionInfo.scenario.raw_material_supplier_id)) {

                        const conditionData = conditionInfo.scenario.scenarioConditions[descriptorId]
                            .find(sceCondition => sceCondition.condition_id === conditionId);
                        augmentedCondition.shipmentsBySubstanceId[conditionInfo.substance.id].push(
                            {
                                ...shipmentsById[conditionInfo.scenario.raw_material_supplier_id],
                                notes: conditionData.notes,
                                value: conditionData.value,
                                unit: conditionData.unit_id ?
                                    unitsById[conditionData.unit_id].name : ''
                            }
                        );
                    }
                } else {
                    augmentedCondition.substanceIds.push(conditionInfo.substance.id);
                    augmentedCondition.substances.push(conditionInfo.substance);
                    augmentedCondition.shipmentIds.push(conditionInfo.scenario.raw_material_supplier_id);

                    const conditionData = conditionInfo.scenario.scenarioConditions[descriptorId]
                        .find(sceCondition => sceCondition.condition_id === conditionId);
                    augmentedCondition.shipmentsBySubstanceId[conditionInfo.substance.id] = [
                        {
                            ...shipmentsById[conditionInfo.scenario.raw_material_supplier_id],
                            notes: conditionData.notes,
                            value: conditionData.value,
                            unit: conditionData.unit_id ?
                                unitsById[conditionData.unit_id].name : ''
                        }
                    ];
                }
            });

            /**
             * linking conditions with respective SUBSTANCE -- SHIPMENT information
             */
            if (augmentedConditionsByDescriptorId[descriptorId][conditionId]) {
                augmentedConditionsByDescriptorId[descriptorId][conditionId].push({
                    ...conditionsById[conditionId],
                    ...augmentedCondition
                });
            } else {
                augmentedConditionsByDescriptorId[descriptorId][conditionId] = [{
                    ...conditionsById[conditionId],
                    ...augmentedCondition
                }];
            }
        });
    });

    return {
        augmentedDescriptors,
        augmentedConditionsByDescriptorId
    };

};
